import React, { useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "kf-proptypes/widgets";
import replaceToArray from "string-replace-to-array";

import { isEmpty } from "base/utils";

import {
  LINE_SEPARATOR,
  CUSTOM_BLOCK_TYPES,
  FORMAT_TYPES,
  TAG_TYPES,
  TAG_TO_FORMAT_MAP
} from "@widgets/common/components/primitives/richtext/util/constants.js";
import { MentionComponent } from "./mention.jsx";
import Mark from "mark.js";
import { Tooltip } from "@widgets/feedback/index.jsx";
import { ICON_TYPES, Icon } from "@widgets/datadisplay/index.jsx";

import styles from "./readonly.editor.css";

import CopyLinkIcon from "../icons/CopyLink.svg";

let imageBaseUrl = `/upload/2/`;
let customRenderer = null;
let history = null;

const EMOJI_ONLY_REGEX =
  /^[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F9B0}-\u{1F9B3}]*$/u;

const EMOJI_INLINE_REGEX =
  /([\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F9B0}-\u{1F9B3}]+)/gu;

const TAG_DICT = {
  strong: (props) => <strong {...props} />,
  em: (props) => <em {...props} />,
  u: (props) => <u {...props} />,
  sub: (props) => <sub {...props} />,
  sup: (props) => <sup {...props} />,
  strike: (props) => <strike {...props} />,
  block_quote: (props) => (
    <div
      {...props}
      className={`${props.className || ""} ${styles.blockquote}`}
    />
  ),
  pre: (props) => <pre {...props} />,
  default: (props) => <span {...props} />
};

ReadonlyHTMLEditor.propTypes = {
  value: PropTypes.string,
  renderer: PropTypes.shape({ field: PropTypes.func, image: PropTypes.func }),
  imageUrl: PropTypes.string,
  className: PropTypes.string,
  searchText: PropTypes.string,
  isEditable: PropTypes.bool
};

/**
 * isEditable prop is temporary one. Once we implement UI/UX changes in TextArea, this will be changed.
 */
export function ReadonlyHTMLEditor({
  value,
  renderer,
  imageUrl,
  className,
  searchText,
  isEditable
}) {
  history = useHistory();
  const readonlyEditorRef = useRef();

  if (imageUrl) {
    imageBaseUrl = imageUrl;
  }
  customRenderer = renderer;

  const editorValue = useMemo(
    function modifyEditorValue() {
      if (value) {
        const parser = new DOMParser();
        const html = parser.parseFromString(value, "text/html");
        const nodes = [...html.body.childNodes];
        return renderParentNodes(nodes, isEditable);
      }
      return [];
      // return modifyEditorNodeValues(value);
    },
    [value, isEditable]
  );

  useEffect(
    function updateSearchWord() {
      const instance = new Mark(readonlyEditorRef.current);
      if (searchText) {
        let separateWordSearch = !(
          searchText.length > 1 &&
          searchText.charAt(0) === '"' &&
          searchText.charAt(searchText.length - 1) === '"'
        );
        instance.unmark({
          separateWordSearch
        });
        setTimeout(() => {
          instance.mark(searchText, {
            separateWordSearch
          });
        });
      } else {
        instance.unmark({
          separateWordSearch: false
        });
      }
    },
    [searchText]
  );

  return (
    <div
      className={`${styles.readonlyeditor} ${className} ql-editor ph-no-capture`}
      ref={readonlyEditorRef}
      data-ls-disabled
      data-openreplay-obscured
      translate="no"
    >
      {editorValue}
      <div style={{ clear: "both" }} />
      {/* To make parent to take full height when children is float */}
    </div>
  );
}

function renderParentNodes(nodes, isEditable) {
  if (!nodes?.length) {
    return [];
  }
  return nodes.map((node, index) => {
    if (!node) {
      return null;
    }
    const { type, childNodes, color, data, align, className } =
      getNodeInfo(node);

    if (checkIfCustomRendererisPresent(type)) {
      return (
        <RenderCustomNode
          type={type}
          className={className}
          color={color}
          data={data}
          align={align}
          childNodes={childNodes}
          index={index}
          key={index}
        />
      );
    } else {
      switch (type) {
        case TAG_TYPES.P:
          return (
            <p
              key={index}
              id={`${index}`}
              style={{ color, textAlign: align }}
              data-paragraph
              data-type={type}
              className={styles.paragraph}
            >
              {renderLeavesNodes(childNodes, type, null, isEditable)}
            </p>
          );
        case TAG_TYPES.H1:
          return (
            <h1
              data-h1
              className={"h3600"}
              key={index}
              style={{ textAlign: align }}
            >
              {renderLeavesNodes(childNodes, type)}
            </h1>
          );
        case TAG_TYPES.H2:
          return (
            <h2
              data-h2
              className={"h4600"}
              key={index}
              style={{ textAlign: align }}
            >
              {renderLeavesNodes(childNodes, type)}
            </h2>
          );
        case TAG_TYPES.H3:
          return (
            <h3
              data-h3
              className={"h3"}
              key={index}
              style={{ textAlign: align }}
            >
              {renderLeavesNodes(childNodes, type)}
            </h3>
          );
        case TAG_TYPES.UL:
          return (
            <ul data-ul className={className} key={index}>
              {renderLeavesNodes(childNodes, type)}
            </ul>
          );
        case TAG_TYPES.OL:
          return (
            <ol data-ol key={index} className={className}>
              {renderLeavesNodes(childNodes, type)}
            </ol>
          );
        case TAG_TYPES.BLOCKQUOTE:
          return (
            <blockquote
              data-blockquote
              key={index}
              className={`${className} ${styles.blockquote}`}
            >
              {renderLeavesNodes(childNodes, type)}
            </blockquote>
          );
        case TAG_TYPES.IMAGE:
          // const customClassnames =
          //   getClassnameForImageFormatting(
          //     getActiveImageFormattingType(node.data.attributes)
          //   ) || "";
          const imageUrl = (data && data.src && getUrl(data.src)) || "";
          return (
            <div
              data-image
              key={index}
              className={`${className} imageWrapper`}
              style={{ textAlign: align }}
            >
              <img src={imageUrl} alt={imageUrl} />
            </div>
          );
        case TAG_TYPES.VIDEO:
          const videoUrl = getProviderUrl(data.url || data);
          return (
            <div
              data-video
              key={index}
              className={`${className}`}
              style={{ textAlign: align }}
            >
              {videoUrl.isNormalVideo ? (
                <video width="640" height="360" controls>
                  <source src={videoUrl.url} />
                </video>
              ) : (
                <iframe
                  title={videoUrl.url}
                  src={videoUrl.url}
                  allowFullScreen
                  frameBorder={0}
                  width="100%"
                  height="360"
                />
              )}
            </div>
          );
        default:
          if (className.includes("ql-code_block")) {
            return (
              <div
                data-codeblock
                key={index}
                className={`${className} ${styles.code_block}`}
              >
                {renderLeavesNodes(childNodes, FORMAT_TYPES.CODE)}
              </div>
            );
          }
          return null;
      }
    }
  });
}

function renderLeavesNodes(nodes, parentType, customRendererNode, isEditable) {
  if (isEmpty(nodes) && customRendererNode) {
    return <span className={styles.text}>{customRendererNode}</span>;
  }
  return (
    nodes &&
    nodes.map((node, index) => {
      if (!node) {
        return null;
      }
      const { type, childNodes, color, data, align, className } =
        getNodeInfo(node);
      if (checkIfCustomRendererisPresent(type)) {
        return (
          <RenderCustomNode
            type={type}
            className={className}
            color={color}
            data={data}
            align={align}
            childNodes={childNodes}
            index={index}
            key={index}
          />
        );
      } else {
        switch (type) {
          case TAG_TYPES.P:
            return (
              <p
                data-paragraph
                key={index}
                className={className}
                style={{ textAlign: align }}
              >
                {renderLeavesNodes(childNodes, type, customRendererNode)}
              </p>
            );
          case TAG_TYPES.PARAGRAPH:
            return (
              <p
                data-paragraph
                key={index}
                className={className}
                style={{ textAlign: align }}
              >
                {renderLeavesNodes(childNodes, type, customRendererNode)}
              </p>
            );
          case TAG_TYPES.LI:
            return (
              <li
                data-li
                key={index}
                className={className}
                style={{ textAlign: align }}
              >
                {renderLeavesNodes(childNodes, type, customRendererNode)}
              </li>
            );
          case TAG_TYPES.TEXT_HASH:
          case TAG_TYPES.TEXT:
            const nodeText = (node.nodeValue || node.text || "")
              .replace(new RegExp(`${LINE_SEPARATOR}$`), "\n\n")
              .replace(new RegExp(LINE_SEPARATOR, "g"), "\n");
            if (EMOJI_ONLY_REGEX.test(nodeText)) {
              return (
                <span className={`${styles.text} ${styles.largeemoji}`}>
                  {nodeText}
                </span>
              );
            }
            return (
              <span className={styles.text}>
                {replaceToArray(nodeText, EMOJI_INLINE_REGEX, (emoji) => {
                  return <span className={styles.inlineemoji}>{emoji}</span>;
                })}
              </span>
            );
          case TAG_TYPES.MENTION:
            return <MentionComponent data={data} />;
          case TAG_TYPES.A:
            let target = "_blank";
            let urlPathName = null;
            let linkData = node?.dataset?.link
              ? JSON.parse(node.dataset.link)
              : { href: node.href };
            if (linkData.href) {
              try {
                const href = new URL(linkData.href);
                if (href.host === window.location.host) {
                  target = "_self";
                  urlPathName = href.pathname.replace("/view", "");
                }
              } catch (e) {
                console.info("url error", e);
              }
            }
            function onClickHandler(e) {
              if (urlPathName) {
                history.push(urlPathName);
                e.preventDefault();
              }
            }
            if (linkData?.href && isEditable) {
              return (
                <Tooltip
                  className={`${styles.linkTooltipOverlay}`}
                  content={
                    <div className={styles.linkTooltipContent}>
                      <Icon
                        type={ICON_TYPES.BASE}
                        paddingNone={true}
                        clickable={false}
                      >
                        <CopyLinkIcon className={styles.linkTooltipIcon} />
                      </Icon>
                      <a
                        className={`${styles.linkTooltipText} ellipsis3`}
                        href={linkData?.href}
                        target={target}
                        onFocus={(e) => {
                          e.stopPropagation();
                        }}
                        onClick={onClickHandler}
                      >
                        {linkData?.href}
                      </a>
                    </div>
                  }
                  arrowPointAtCenter={true}
                >
                  <a
                    onClick={onClickHandler}
                    href={linkData?.href}
                    target={target}
                    rel="noreferrer"
                    style={{
                      color,
                      textAlign: align,
                      cursor: isEditable ? "pointer" : "text"
                    }}
                  >
                    {renderLeavesNodes(childNodes, type, customRendererNode)}
                  </a>
                </Tooltip>
              );
            } else {
              return (
                <a
                  onClick={onClickHandler}
                  href={linkData?.href}
                  target={target}
                  rel="noreferrer"
                  style={{
                    color,
                    textAlign: align
                  }}
                >
                  {renderLeavesNodes(childNodes, type, customRendererNode)}
                </a>
              );
            }

          default:
            let Tag =
              TAG_DICT[
                parentType === TAG_TYPES.BLOCKQUOTE
                  ? FORMAT_TYPES.BLOCKQUOTE
                  : type
              ] || TAG_DICT.default;
            return (
              <Tag
                type={type}
                key={index}
                className={className}
                style={{
                  color,
                  textAlign: align,
                  fontSize: data.size || "inherit"
                }}
              >
                {renderLeavesNodes(childNodes, type, customRendererNode)}
              </Tag>
            );
        }
      }
    })
  );
}

function getNodeInfo(node) {
  let type = node.nodeName.toLowerCase();
  let dataset = node.dataset;
  let className = node.className || "";
  let color = node.style?.color || "";
  let align = "left";
  let data = {};
  let childNodes = [];

  if (dataset?.type) {
    type = dataset.type;
  }
  if (dataset?.align) {
    align = dataset.align;
  }
  if (dataset?.[type]) {
    data = JSON.parse(dataset[type]);
  }
  if (node?.childNodes?.length) {
    childNodes = [...node.childNodes];
  }
  return {
    type,
    childNodes,
    color,
    data,
    align,
    className
  };
}

function checkIfCustomRendererisPresent(type) {
  if (!customRenderer) return false;
  if (customRenderer[type]) return customRenderer[type];
  if (customRenderer[TAG_TO_FORMAT_MAP[type]])
    return customRenderer[TAG_TO_FORMAT_MAP[type]];
  return false;
}

function getUrl(url) {
  if (url.indexOf("https") === 0) {
    return url;
  } else {
    return imageBaseUrl + url;
  }
}

function getProviderUrl(url) {
  // reference https://regexr.com/3dj5t
  // eslint-disable-next-line max-len
  const YOUTUBE_REGEX =
    /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)?$/;
  const LOOM_REGEX =
    /^((?:https?:)?\/\/)?((?:www|m)\.)?(?:loom\.com\/share\/)([\w-]+)?$/;
  if (url.match(YOUTUBE_REGEX)) {
    let matched = YOUTUBE_REGEX.exec(url);
    let videoId = (matched && matched.length > 0 && matched[5]) || "";
    return { url: `https://www.youtube.com/embed/${videoId}?wmode=opaque` };
  } else if (url.match(/https:\/\/([a-z]*\.)?dailymotion.com/)) {
    let videoId = url.split("/").pop();
    return { url: `https://www.dailymotion.com/embed/video/${videoId}` };
  } else if (url.match(/https:\/\/([a-z]*\.)?vimeo.com/)) {
    let videoId = url.split("/").pop();
    return { url: `https://player.vimeo.com/video/${videoId}` };
  } else if (url.match(LOOM_REGEX)) {
    let matched = LOOM_REGEX.exec(url);
    let videoId = (matched && matched.length > 0 && matched[3]) || "";
    return { url: `https://www.loom.com/embed/${videoId}` };
  }

  return { url, isNormalVideo: true };
}

function RenderCustomNode({
  node,
  type,
  color,
  data,
  className,
  align,
  childNodes,
  index
}) {
  const CustomNodeComponent = checkIfCustomRendererisPresent(type);
  const Wrapper = CUSTOM_BLOCK_TYPES.includes(type) ? "div" : "span";

  // const nodeObj = useMemo(
  //   function get() {

  //     if (type === FORMAT_TYPES.IMAGE && node.dataset?.attributes) {
  //       setCustomClassnames(
  //         getClassnameForImageFormatting(
  //           getActiveImageFormattingType(node.data.attributes)
  //         ) || ""
  //       );
  //     }
  //   },
  //   []
  // );

  return (
    <Wrapper className={`${className}`} style={{ color }}>
      <CustomNodeComponent
        key={index}
        node={{ data, type, nodes: childNodes, align }}
        index={index}
        callBack={renderLeavesNodes}
      />
    </Wrapper>
  );
}

RenderCustomNode.propTypes = {
  node: PropTypes.arrayOf(PropTypes.string),
  index: PropTypes.number,
  type: PropTypes.string,
  color: PropTypes.string,
  data: PropTypes.shape({
    src: PropTypes.string
  }),
  className: PropTypes.string,
  align: PropTypes.string,
  childNodes: PropTypes.arrayOf(PropTypes.node)
};
